import {
  Component,
  OnInit,
  OnDestroy,
  ChangeDetectorRef,
  ViewEncapsulation,
} from '@angular/core';
import { RouterModule, Router } from '@angular/router';
import { CommonModule } from '@angular/common';
import { FormsModule, NgForm } from '@angular/forms';
import { Observable, of } from 'rxjs';
import { catchError, finalize, map } from 'rxjs/operators';

import { CompaniesService } from './companies.service';
import { Company } from '../models/companies.model';
import { CompaniesResponse } from '../models/companies-response.model';
import { AppConfigTopNavComponent } from '../../../shared/app-config-nav/app-config-top-nav.component';
import { SearchBarComponent } from '../../../shared/search-bar/search-bar.component';

interface CompanyFiltersVm {
  searchTerm: string;
}

@Component({
  selector: 'app-companies',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    AppConfigTopNavComponent,
    RouterModule,
    SearchBarComponent,
  ],
  templateUrl: './companies.component.html',
  styleUrls: ['./companies.component.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class CompaniesComponent implements OnInit, OnDestroy {
  companies$: Observable<Company[]> = of([]);
  isLoading = false;
  errorMessage: string | null = null;

  currentPage = 1;
  pageSize = 25;
  pageSizeOptions = [25, 50, 100, 500];
  totalPages = 1;

  filters: CompanyFiltersVm = {
    searchTerm: '',
  };

  modalOpen = false;
  isEditMode = false;
  currentCompany: Company | null = null;
  formModel: Partial<Company> = this.getEmptyCompany();

  deleteConfirmOpen = false;
  companyToDelete: Company | null = null;

  toastVisible = false;
  toastMessage = '';
  toastType: 'success' | 'error' = 'success';

  constructor(
    private readonly companiesService: CompaniesService,
    private readonly cd: ChangeDetectorRef,
    private readonly router: Router,
  ) {}

  ngOnInit(): void {
    this.loadCompanies();
  }

  ngOnDestroy(): void {
    this.unlockBodyScroll();
  }

  private lockBodyScroll(): void {
    document.body.style.overflow = 'hidden';
  }

  private unlockBodyScroll(): void {
    document.body.style.overflow = '';
  }

  loadCompanies(): void {
    this.isLoading = true;
    this.errorMessage = null;

    this.companies$ = this.companiesService
      .getCompanies(this.currentPage, this.pageSize, 'name', {
        searchTerm: this.filters.searchTerm,
      })
      .pipe(
        map((res: CompaniesResponse | null) => {
          if (!res) {
            this.totalPages = 1;
            return [];
          }

          this.totalPages = res.totalPages ?? 1;
          return res.data ?? [];
        }),
        catchError(() => {
          this.errorMessage = 'Failed to load companies';
          this.totalPages = 1;
          return of([]);
        }),
        finalize(() => {
          this.isLoading = false;
          this.cd.detectChanges();
        }),
      );
  }

  onSearchTermChange(value: string | null): void {
    this.filters.searchTerm = String(value ?? '').trim();
    this.currentPage = 1;
    this.loadCompanies();
  }

  nextPage(): void {
    if (this.currentPage < this.totalPages) {
      this.currentPage++;
      this.loadCompanies();
    }
  }

  prevPage(): void {
    if (this.currentPage > 1) {
      this.currentPage--;
      this.loadCompanies();
    }
  }

  changePageSize(): void {
    this.currentPage = 1;
    this.loadCompanies();
  }

  openCreateModal(): void {
    this.isEditMode = false;
    this.currentCompany = null;
    this.formModel = this.getEmptyCompany();
    this.modalOpen = true;

    this.lockBodyScroll();
  }

  openEditModal(company: Company): void {
    this.isEditMode = true;
    this.currentCompany = company;
    this.formModel = { ...company };
    this.modalOpen = true;

    this.lockBodyScroll();
  }

  closeModal(): void {
    this.modalOpen = false;
    this.isEditMode = false;
    this.currentCompany = null;
    this.formModel = this.getEmptyCompany();

    if (!this.deleteConfirmOpen) {
      this.unlockBodyScroll();
    }
  }

  submitCompany(form?: NgForm): void {
    if (!this.formModel.name) {
      this.showToast('Name is required', 'error');
      return;
    }

    const payload: Partial<Company> = {
      ...this.formModel,
      isSubtractor: !!this.formModel.isSubtractor,
    };

    const request$ =
      this.isEditMode && this.currentCompany
        ? this.companiesService.updateCompany(this.currentCompany.id, payload)
        : this.companiesService.createCompany(payload);

    request$.subscribe({
      next: () => {
        this.closeModal();

        this.showToast(
          this.isEditMode
            ? 'Company updated successfully'
            : 'Company created successfully',
          'success',
        );

        this.loadCompanies();
        this.cd.detectChanges();
      },
      error: () => {
        this.showToast(
          this.isEditMode
            ? 'Failed to update company'
            : 'Failed to create company',
          'error',
        );

        this.cd.detectChanges();
      },
    });
  }

  confirmDelete(company: Company): void {
    this.companyToDelete = company;
    this.deleteConfirmOpen = true;

    this.lockBodyScroll();
  }

  cancelDelete(): void {
    this.deleteConfirmOpen = false;
    this.companyToDelete = null;

    if (!this.modalOpen) {
      this.unlockBodyScroll();
    }
  }

  deleteCompany(): void {
    if (!this.companyToDelete) {
      return;
    }

    this.companiesService.deleteCompany(this.companyToDelete.id).subscribe({
      next: () => {
        this.cancelDelete();

        this.showToast(
          'Company deleted successfully',
          'success',
        );

        this.loadCompanies();
        this.cd.detectChanges();
      },
      error: () => {
        this.showToast(
          'Failed to delete company',
          'error',
        );

        this.cd.detectChanges();
      },
    });
  }

  goToImport(): void {
    this.router.navigate(['/settings/application/companies/import']);
  }

  showToast(message: string, type: 'success' | 'error'): void {
    this.toastMessage = message;
    this.toastType = type;
    this.toastVisible = true;

    setTimeout(() => {
      this.toastVisible = false;
      this.cd.detectChanges();
    }, 2500);
  }

  private getEmptyCompany(): Partial<Company> {
    return {
      name: '',
      description: '',
      isSubtractor: false,
    };
  }
}